import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, Inject } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { PaginatedRepository } from '../pagination/paginated.repository';
import { REG_HUB_COMMON_CONFIG, RegHubCommonConfig } from '../../reg-hub-common-config';
import { ReusableGeneralCollateral } from '../../interfaces/reusable-general-collateral/reusable-general-collateral';
import { ReusableGeneralCollateralForCreation } from '../../interfaces/reusable-general-collateral/reusable-general-collateral-for-creation';
import { ReusableGeneralCollateralForUpdate } from '../../interfaces/reusable-general-collateral/reusable-general-collateral-for-update';

@Injectable({
  providedIn: 'root'
})
export class ReusableGeneralCollateralRepositoryService extends PaginatedRepository<ReusableGeneralCollateral> {
  protected override _baseLink: string;

  constructor(
      http: HttpClient,
      messageService: ToastrService,
      @Inject(REG_HUB_COMMON_CONFIG) private config: RegHubCommonConfig) {
      
      super(http,messageService);

      this._currentParams = new HttpParams()
          .set('pageSize', 10);

      this._baseLinkTemplate = `${config.rootUrl}/Corporations/[0]/ReusableGeneralCollaterals`;
      this._baseLink = this._baseLinkTemplate;
  }

  // Lists the corporation's general collateral, or the user group's when a userGroupID is given
  public populateScopedBaseLink(corporationID: string, userGroupID?: string | null) {
      var urlSlug = `/Corporations/${corporationID}`
      if (userGroupID) {
        urlSlug = `${urlSlug}/UserGroups/${userGroupID}`
      }

      this._baseLink = `${this.config.rootUrl}${urlSlug}/ReusableGeneralCollaterals`;
  }

  private generateHeaders = () => {
      return {
          headers: new HttpHeaders({ 'Content-Type': 'application/json' })
      }
  }

  public createReusableGeneralCollateral = (corporationID: string, reusableGeneralCollateral: ReusableGeneralCollateralForCreation, userGroupID?: string | null) => {
      var urlSlug = `/Corporations/${corporationID}`
      if (userGroupID) {
        urlSlug = `${urlSlug}/UserGroups/${userGroupID}`
      }

      return this.http.post<ReusableGeneralCollateral>(
          `${this.config.rootUrl}${urlSlug}/ReusableGeneralCollaterals`,
          reusableGeneralCollateral,
          this.generateHeaders());
  }

  public getReusableGeneralCollateral = (corporationID: string, reusableGeneralCollateralID: string, userGroupID?: string | null) => {
      var urlSlug = `/Corporations/${corporationID}`
      if (userGroupID) {
        urlSlug = `${urlSlug}/UserGroups/${userGroupID}`
      }

      return this.http.get<ReusableGeneralCollateral>(
          `${this.config.rootUrl}${urlSlug}/ReusableGeneralCollaterals/${reusableGeneralCollateralID}`,
          this.generateHeaders());
  }

  // usableOnly: the collateral usable at this level - the user group's own (or the corporation's when the group falls back to it),
  // or only the corporation level collateral when no user group is given
  public getReusableGeneralCollaterals = (corporationID: string, userGroupID?: string | null, usableOnly: boolean = false) => {
    var urlSlug = `/Corporations/${corporationID}`
    var scope = '';
    if (userGroupID) {
      urlSlug = `${urlSlug}/UserGroups/${userGroupID}`
      scope = usableOnly ? '&UseCorporationFallback=true' : '';
    } else {
      scope = usableOnly ? '&CorporationLevelOnly=true' : '';
    }

    return this.http.get<ReusableGeneralCollateral[]>(
        `${this.config.rootUrl}${urlSlug}/ReusableGeneralCollaterals?IsVisible=true${scope}`,
        this.generateHeaders());
  }

  public updateReusableGeneralCollateral = (corporationID: string, reusableGeneralCollateralID: string, reusableGeneralCollateral: ReusableGeneralCollateralForUpdate, userGroupID?: string | null) => {
      var urlSlug = `/Corporations/${corporationID}`
      if (userGroupID) {
        urlSlug = `${urlSlug}/UserGroups/${userGroupID}`
      }

      return this.http.put<ReusableGeneralCollateralForUpdate>(
          `${this.config.rootUrl}${urlSlug}/ReusableGeneralCollaterals/${reusableGeneralCollateralID}`,
          reusableGeneralCollateral,
          this.generateHeaders());
  }

  public deleteReusableGeneralCollateral = (corporationID: string, reusableGeneralCollateralID: string, userGroupID?: string | null) => {
      var urlSlug = `/Corporations/${corporationID}`
      if (userGroupID) {
        urlSlug = `${urlSlug}/UserGroups/${userGroupID}`
      }

      return this.http.delete(
          `${this.config.rootUrl}${urlSlug}/ReusableGeneralCollaterals/${reusableGeneralCollateralID}`,
          this.generateHeaders());
  }
}
